import { ContentNode } from '@amzn/awscat-pattern-library-service-model';

import { SpecialSectionId } from './SectionOperations';

const RENDERABLE_HTML_ELEMENT = /<(?:audio|hr|iframe|img|table|video)\b/i;
const HTML_TAG = /<[^>]*>/g;
const HTML_WHITESPACE_ENTITY = /&(?:nbsp|#160|#x0*a0);/gi;
const RENDERABLE_TIPTAP_NODE_TYPES = new Set([
  'entity',
  'horizontalRule',
  'image',
  'mention',
  'table',
  'video',
  'youtube',
]);

function isRecord(value: object): value is Record<string, unknown> {
  return !Array.isArray(value);
}

function hasRenderableObjectContent(value: Record<string, unknown>): boolean {
  if (typeof value.text === 'string' && value.text.trim().length > 0) return true;

  if (typeof value.type === 'string' && RENDERABLE_TIPTAP_NODE_TYPES.has(value.type)) {
    return true;
  }

  return Object.entries(value).some(([key, child]) => {
    if (key === 'type' || key === 'attrs' || key === 'marks') return false;
    return hasRenderableRichText(child);
  });
}

/** Whether rich text produces visible output in the read-only viewer. */
export function hasRenderableRichText(value: unknown): boolean {
  if (value == null) return false;

  if (typeof value === 'string') {
    if (RENDERABLE_HTML_ELEMENT.test(value)) return true;
    return value.replace(HTML_TAG, '').replace(HTML_WHITESPACE_ENTITY, ' ').trim().length > 0;
  }

  if (Array.isArray(value)) return value.some(hasRenderableRichText);
  if (typeof value === 'object' && isRecord(value)) {
    return hasRenderableObjectContent(value);
  }

  return false;
}

/** Whether a section has content worth displaying in a read-only view. */
export function isSectionRenderable(section: ContentNode): boolean {
  if (section.id === SpecialSectionId.Epics || section.id === SpecialSectionId.Troubleshooting) {
    return Array.isArray(section.contentData) && section.contentData.length > 0;
  }

  return hasRenderableRichText(section.contentData);
}

/** Removes empty sections while preserving document order. */
export function filterRenderableSections(sections: readonly ContentNode[]): readonly ContentNode[] {
  return sections.filter(isSectionRenderable);
}
